// src/pages/Home2.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// === VIDEOS & IMAGES ===
import smVideo from '../assets/River/MARR compressed.mp4';
import lgVideo from '../assets/River/MARR compressed.mp4';

import goldImage from '../assets/River/20.jpeg';
import silverImage from '../assets/River/47.jpeg';
import preciousImage from '../assets/River/37.webp';
import semiPreciousImage from '../assets/Tours/bentota-river-safari.jpg';

import gallery1 from '../assets/River/17.jpeg';
import gallery2 from '../assets/River/12.jpeg';
import gallery3 from '../assets/River/38.webp';
import gallery4 from '../assets/River/25.jpeg';
import gallery5 from '../assets/Ayurveda/39.jpeg';
import gallery6 from '../assets/River/32.png';
import gallery7 from '../assets/Ayurveda/59.jpeg';
import gallery8 from '../assets/River/15.jpeg';
import gallery9 from '../assets/Ayurveda/69.jpeg';
import gallery10 from '../assets/River/13.jpeg';
import gallery11 from '../assets/Ayurveda/IMG_1803.jpeg';
import gallery12 from '../assets/River/30.jpeg';

import kandy1 from '../assets/River/3.jpeg';
import kandy2 from '../assets/River/14.jpeg';
import kandy3 from '../assets/River/28.jpeg';
import kandy4 from '../assets/Ayurveda/44.jpeg';
import kandy5 from '../assets/River/18.jpeg';

import room2 from '../assets/River/19.jpeg';
import room3 from '../assets/River/45.jpeg';

import boatRideImage from '../assets/River/43.jpg';
import poolArea from '../assets/River/11.jpeg';

// === FRAMER MOTION VARIANTS ===
const textVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};

const slideLeft = {
    hidden: { opacity: 0, x: 50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};

const scaleUp = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.8, ease: 'easeOut' } },
};

const fadeInLeft = {
    hidden: { opacity: 0, x: -50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};

const fadeInRight = {
    hidden: { opacity: 0, x: 50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};

const staggerContainer = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { staggerChildren: 0.2 } },
};

const accordionItem = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const Home2 = () => {
    const { t } = useTranslation('home2');
    const [activeIndex, setActiveIndex] = useState(null);
    const [showBookingBar, setShowBookingBar] = useState(false);
    const today = new Date();
    const minimumCheckout = new Date();
    minimumCheckout.setDate(today.getDate() + 3);

    const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const [checkInDate, setCheckInDate] = useState(formatDate(today));
    const [checkOutDate, setCheckOutDate] = useState(formatDate(minimumCheckout));

    const toggleService = (index) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            setShowBookingBar(true);
        }, 800);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        }
    }, []);

    const handleClose = () => {
        setShowBookingBar(false);
    };

    const handleCheckInChange = (event) => {
        const newCheckIn = new Date(event.target.value);
        const currentCheckOut = new Date(checkOutDate);
        const minCheckOut = new Date(newCheckIn);
        minCheckOut.setDate(minCheckOut.getDate() + 3);

        setCheckInDate(event.target.value);

        if (currentCheckOut < minCheckOut) {
            setCheckOutDate(formatDate(minCheckOut));
        }
    };

    const handleCheckOutChange = (event) => {
        const newCheckOut = new Date(event.target.value);
        const currentCheckIn = new Date(checkInDate);
        const minCheckOut = new Date(currentCheckIn);
        minCheckOut.setDate(minCheckOut.getDate() + 3);

        if (newCheckOut >= minCheckOut) {
            setCheckOutDate(event.target.value);
        }
    };

    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://elfsightcdn.com/platform.js';
        script.async = true;
        document.body.appendChild(script);

        script.onload = () => console.log('Elfsight script loaded');
        script.onerror = () => console.error('Elfsight script failed');

        return () => {
            if (document.body.contains(script)) document.body.removeChild(script);
        };
    }, []);

    // Dynamic translated content
    const getServices = () => [
        { title: t('service1Title'), desc: t('service1Desc') },
        { title: t('service2Title'), desc: t('service2Desc') },
        { title: t('service3Title'), desc: t('service3Desc') },
        { title: t('service4Title'), desc: t('service4Desc') },
        { title: t('service5Title'), desc: t('service5Desc') },
    ];

    const getAchievements = () => [
        { title: t('achievement1Title'), count: t('achievement1Count') },
        { title: t('achievement2Title'), count: t('achievement2Count') },
        { title: t('achievement3Title'), count: t('achievement3Count') },
        { title: t('achievement4Title'), count: t('achievement4Count') },
    ];

    const getExperiences = () => [
        {
            img: goldImage,
            title: t('experience1Title'),
            desc: t('experience1Desc'),
            link: '/river/rooms'
        },
        {
            img: silverImage,
            title: t('experience2Title'),
            desc: t('experience2Desc'),
            link: '/river/ayurveda'
        },
        {
            img: preciousImage,
            title: t('experience3Title'),
            desc: t('experience3Desc'),
            link: '/river/food'
        },
        {
            img: semiPreciousImage,
            title: t('experience4Title'),
            desc: t('experience4Desc'),
            link: '/river/tours'
        },
    ];

    const getRooms = () => [
        {
            title: t('room1Title'),
            desc: t('room1Desc'),
            image: room2,
            link: '/river/rooms/deluxe-room'
        },
        {
            title: t('room2Title'),
            desc: t('room2Desc'),
            image: room3,
            link: '/river/rooms/deluxe-river-view-room'
        },
    ];

    const getGalleryImages = () => [
        { src: gallery1, alt: t('gallery1Alt') },
        { src: gallery2, alt: t('gallery2Alt') },
        { src: gallery3, alt: t('gallery3Alt') },
        { src: gallery4, alt: t('gallery4Alt') },
        { src: gallery5, alt: t('gallery5Alt') },
        { src: gallery6, alt: t('gallery6Alt') },
        { src: gallery7, alt: t('gallery7Alt') },
        { src: gallery8, alt: t('gallery8Alt') },
        { src: gallery9, alt: t('gallery9Alt') },
        { src: gallery10, alt: t('gallery10Alt') },
        { src: gallery11, alt: t('gallery11Alt') },
        { src: gallery12, alt: t('gallery12Alt') },
    ];

    const getResortImages = () => [
        { src: kandy1, alt: t('resort1Alt') },
        { src: kandy2, alt: t('resort2Alt') },
        { src: kandy3, alt: t('resort3Alt') },
        { src: kandy4, alt: t('resort4Alt') },
        { src: kandy5, alt: t('resort5Alt') },
    ];

    const services = getServices();
    const achievements = getAchievements();
    const experiences = getExperiences();
    const rooms = getRooms();
    const galleryImages = getGalleryImages();
    const resortImages = getResortImages();

    const kandySliderSettings = {
        dots: false,
        infinite: true,
        speed: 800,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 4000,
        arrows: false,
        appendDots: (dots) => (
            <div className="mt-4">
                <ul className="flex justify-center gap-2">{dots}</ul>
            </div>
        ),
        customPaging: () => (
            <div className="w-3 h-3 rounded-full bg-white hover:bg-[#BF943A] transition"></div>
        ),
    };

    const mobileSettings = {
        dots: false,
        infinite: true,
        speed: 800,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: true,
        appendDots: (dots) => (
            <div className="mt-6">
                <ul className="flex justify-center gap-2">{dots}</ul>
            </div>
        ),
        customPaging: () => (
            <div className="w-2 h-2 rounded-full bg-white opacity-70 hover:opacity-100 transition"></div>
        ),
        prevArrow: (
            <div className="slick-prev !left-2 !w-14 !h-14 !flex !items-center !justify-center !bg-white/80 !rounded-full hover:!bg-[#BF943A] !text-black hover:!text-white transition-all !shadow-lg z-20">
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M15 19l-7-7 7-7" />
                </svg>
            </div>
        ),
        nextArrow: (
            <div className="slick-next !right-2 !w-14 !h-14 !flex !items-center !justify-center !bg-white/80 !rounded-full hover:!bg-[#BF943A] !text-black hover:!text-white transition-all !shadow-lg z-20">
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                </svg>
            </div>
        ),
    };

    const tabSettings = {
        dots: false,
        infinite: true,
        speed: 800,
        slidesToShow: 2,
        slidesToScroll: 1,
        arrows: false,
        appendDots: (dots) => (
            <div className="mt-6">
                <ul className="flex justify-center gap-2">{dots}</ul>
            </div>
        ),
        customPaging: () => (
            <div className="w-2 h-2 rounded-full bg-white opacity-70 hover:opacity-100 transition"></div>
        ),
    };

    const galleryMobileSettings = {
        dots: false,
        infinite: true,
        speed: 800,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: false,
        autoplay: true,
        autoplaySpeed: 3000,
        appendDots: (dots) => (
            <div className="mt-4">
                <ul className="flex justify-center gap-2">{dots}</ul>
            </div>
        ),
        customPaging: () => (
            <div className="w-2 h-2 rounded-full bg-white opacity-70 hover:opacity-100 transition"></div>
        ),
    };

    return (
        <div className="min-h-screen bg-[#1F1F1F] text-white">
            {/* Hero Section */}
            <div className="relative flex h-[95vh] w-full items-center justify-center font-redhat lg:h-screen">
                <video
                    src={smVideo}
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="absolute inset-0 h-full w-full object-cover block sm:hidden"
                />
                <video
                    src={lgVideo}
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="absolute inset-0 h-full w-full object-cover hidden sm:block"
                />
                <div className="absolute inset-0 bg-black/60" />

                {/* Booking Bar */}
                <AnimatePresence>
                    {showBookingBar && (
                        <motion.div
                            initial={{ y: -100, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: -100, opacity: 0 }}
                            transition={{ duration: 0.7, ease: "easeOut", delay: 0.2 }}
                            className="absolute bottom-0 left-0 right-0 z-50 bg-[#285509] backdrop-blur-md border-t border-[#BF943A]/40 shadow-2xl"
                        >
                            <div className="container mx-auto px-4 py-6 lg:py-8">
                                <div className="flex flex-col gap-4 md:gap-0">
                                    <div className="flex justify-end mb-2 md:mb-0">
                                        <button
                                            onClick={handleClose}
                                            className="text-white hover:text-[#BF943A] transition text-2xl font-light w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#0f2219]/50"
                                            aria-label="Close"
                                        >
                                            ×
                                        </button>
                                    </div>

                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5 lg:gap-6 lg:grid-cols-5 lg:items-end">
                                        <div className="flex flex-col gap-2">
                                            <label className="text-xs font-semibold text-white uppercase tracking-wider">
                                                {t('bookingDestination')}
                                            </label>
                                            <input
                                                type="text"
                                                placeholder={t('bookingDestinationPlaceholder')}
                                                defaultValue={t('bookingDestinationDefault')}
                                                className="w-full bg-white border border-[#BF943A]/30 rounded-lg px-4 py-3 text-black placeholder-gray-500 focus:outline-none focus:border-[#BF943A] transition text-sm"
                                            />
                                        </div>

                                        <div className="flex flex-col gap-2">
                                            <label className="text-xs font-semibold text-white uppercase tracking-wider">
                                                {t('bookingCheckIn')}
                                            </label>
                                            <input
                                                type="date"
                                                value={checkInDate}
                                                min={formatDate(new Date())}
                                                onChange={handleCheckInChange}
                                                className="w-full bg-white border border-[#BF943A]/30 rounded-lg px-4 py-3 text-black focus:outline-none focus:border-[#BF943A] transition text-sm"
                                            />
                                        </div>

                                        <div className="flex flex-col gap-2">
                                            <label className="text-xs font-semibold text-white uppercase tracking-wider">
                                                {t('bookingCheckOut')}
                                            </label>
                                            <input
                                                type="date"
                                                value={checkOutDate}
                                                min={(() => {
                                                    const minDate = new Date(checkInDate);
                                                    minDate.setDate(minDate.getDate() + 3);
                                                    return formatDate(minDate);
                                                })()}
                                                onChange={handleCheckOutChange}
                                                className="w-full bg-white border border-[#BF943A]/30 rounded-lg px-4 py-3 text-black focus:outline-none focus:border-[#BF943A] transition text-sm"
                                            />
                                        </div>

                                        <div className="flex flex-col gap-2">
                                            <label className="text-xs font-semibold text-white uppercase tracking-wider">
                                                {t('bookingGuests')}
                                            </label>
                                            <select className="w-full bg-white border border-[#BF943A]/30 rounded-lg px-4 py-3 text-black focus:outline-none focus:border-[#BF943A] transition cursor-pointer text-sm">
                                                <option>{t('guestsOption1')}</option>
                                                <option>{t('guestsOption2')}</option>
                                                <option>{t('guestsOption3')}</option>
                                                <option>{t('guestsOption4')}</option>
                                            </select>
                                        </div>

                                        <div className="flex flex-col gap-2 sm:col-span-2 lg:col-span-1">
                                            <div className="h-9"></div>
                                            <Link
                                                to={{
                                                    pathname: '/river/search',
                                                    search: `?checkIn=${checkInDate}&checkOut=${checkOutDate}`
                                                }}
                                                className="w-full bg-gradient-to-r from-[#BF943A] to-[#D4A574] hover:from-[#D4A574] hover:to-[#BF943A] text-black font-bold py-3 px-6 rounded-lg transition duration-300 transform hover:scale-105 shadow-lg text-sm uppercase tracking-wider whitespace-nowrap text-center"
                                            >
                                                {t('bookingCheckAvailability')}
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                <div className="container relative z-10 flex items-center justify-center pt-[100px]">
                    <div className="w-[90%] text-center md:w-[75%] pb-40 lg:pb-32">
                        <motion.h1
                            variants={textVariants}
                            initial="hidden"
                            animate="visible"
                            className="text-[45px] font-semibold leading-[60px] text-white md:text-[55px] md:leading-[80px] lg:text-[70px] font-copperplate"
                        >
                            {t('heroTitle')}
                        </motion.h1>
                        <motion.div
                            variants={textVariants}
                            initial="hidden"
                            animate="visible"
                            className="my-5 text-[18px] font-medium leading-6 md:text-[20px] lg:text-[25px] lg:leading-8"
                        >

                            <span className="text-white"> {t('heroSubtitle')}</span>
                        </motion.div>
                        <motion.div
                            variants={slideLeft}
                            initial="hidden"
                            animate="visible"
                            className="mt-10 flex justify-center gap-4"
                        >
                            <Link
                                to="/river/rooms"
                                className="w-[180px] rounded-full bg-white py-2 text-[14px] font-bold text-black md:text-[16px] lg:text-[18px] hover:bg-gray-200 transition"
                            >
                                {t('exploreResort')}
                            </Link>
                            <Link
                                to="/river/search"
                                className="w-[180px] rounded-full bg-[#BF943A] py-2 text-[14px] font-bold text-black md:text-[16px] lg:text-[18px] hover:bg-[#A07A2F] transition"
                            >
                                {t('bookNow')}
                            </Link>
                        </motion.div>
                    </div>
                </div>
            </div>

            {/* Welcome Section */}
            <section className="bg-[#1F1F1F] py-12 md:py-16 lg:py-24">
                <div className="container font-redhat text-white">
                    {/* Mobile-first layout: label → title → image → content */}
                    <div className="lg:hidden space-y-6 mb-10">
                        <div className="flex items-center gap-4">
                            <div className="h-px w-[40px] bg-[#96BA33]"></div>
                            <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold uppercase text-[#96BA33]">
                                {t('welcomeLabel')}
                            </h2>
                        </div>

                        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight">
                            {t('welcomeTitle')}
                        </h1>

                        <div className="h-px w-[100px] bg-[#BF943A]"></div>
                    </div>

                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={textVariants}
                        className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16"
                    >
                        {/* Text Content */}
                        <div className="w-full lg:w-1/2 space-y-6 order-2 lg:order-1">
                            {/* Desktop label + title – hidden on < lg */}
                            <div className="hidden lg:flex items-center gap-5">
                                <div className="h-px w-[40px] bg-[#96BA33]"></div>
                                <h2 className="text-[20px] lg:text-[35px] xl:text-[45px] font-semibold uppercase text-[#96BA33]">
                                    {t('welcomeLabel')}
                                </h2>
                            </div>

                            <h1 className="hidden lg:block text-[28px] lg:text-[38px] xl:text-[48px] font-bold leading-tight">
                                {t('welcomeTitle')}
                            </h1>

                            <div className="h-px w-[120px] bg-[#BF943A] hidden lg:block"></div>

                            <p className="text-[16px] sm:text-[18px] lg:text-[20px] text-gray-300 leading-relaxed">
                                {t('welcomeDesc1')}
                            </p>

                            <p className="text-[15px] sm:text-[16px] lg:text-[18px] text-gray-400 leading-relaxed">
                                {t('welcomeDesc2')}
                            </p>

                            <div className="pt-4 flex flex-wrap gap-4">
                                <Link
                                    to="/river/about-us"
                                    className="inline-flex items-center gap-2 bg-gradient-to-r from-[#96BA33] to-[#7A9629] hover:from-[#7A9629] hover:to-[#96BA33] text-white font-semibold px-7 py-3 rounded-full transition duration-300 transform hover:scale-105 shadow-lg text-sm sm:text-base uppercase tracking-wider"
                                >
                                    {t('learnMore')}
                                    <span>→</span>
                                </Link>
                                <Link
                                    to="/river/contact-us"
                                    className="inline-flex items-center gap-2 border-2 border-[#BF943A] text-[#BF943A] hover:bg-[#BF943A] hover:text-black font-semibold px-7 py-3 rounded-full transition duration-300 text-sm sm:text-base uppercase tracking-wider"
                                >
                                    {t('contactUs')}
                                </Link>
                            </div>
                        </div>

                        {/* Image */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.3 }}
                            variants={scaleUp}
                            className="w-full lg:w-1/2 order-1 lg:order-2"
                        >
                            <div className="relative h-[360px] sm:h-[420px] md:h-[500px] lg:h-[600px] rounded-2xl overflow-hidden shadow-2xl">
                                <img
                                    src={kandy2}
                                    alt={t('welcomeImageAlt')}
                                    className="h-full w-full object-cover hover:scale-110 transition-transform duration-700"
                                    onError={(e) => { e.target.src = 'https://via.placeholder.com/600x600?text=Muthumuni+Resort'; }}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                                <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6 text-white">
                                    <p className="text-sm sm:text-base font-semibold uppercase tracking-wider">
                                        {t('welcomeImageCaption')}
                                    </p>
                                    <p className="text-xs sm:text-sm text-gray-300 mt-1">
                                        {t('welcomeImageLocation')}
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* Resort Highlight Section */}
            <section className="bg-[#1F1F1F] py-10 md:py-12 lg:py-16">
                <div className="container font-redhat text-white">
                    {/* ── Mobile-only header ──────────────────────────────── */}
                    <div className="lg:hidden space-y-5 mb-8">
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.3 }}
                            variants={textVariants}
                            className="flex items-center gap-4"
                        >
                            <h1 className="text-xl sm:text-2xl font-semibold uppercase">
                                {t('highlightTitle')}
                            </h1>
                        </motion.div>

                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.3 }}
                            variants={textVariants}
                            className="font-semibold text-[#96BA33] text-base sm:text-lg"
                        >
                            {t('highlightDesc')}
                        </motion.div>

                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.3 }}
                            variants={textVariants}
                            className="h-px w-full bg-[#D7D7D7]"
                        />
                    </div>

                    {/* ── Desktop / Tablet header ─────────────────────────── */}
                    <div className="hidden lg:block">
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.3 }}
                            variants={textVariants}
                            className="flex items-center gap-5"
                        >
                            <div className="h-px w-[40px] bg-[#D7D7D7]"></div>
                            <h1 className="text-[15px] md:text-[30px] lg:text-[40px] font-semibold uppercase">
                                {t('highlightTitle')}
                            </h1>
                        </motion.div>

                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.3 }}
                            variants={textVariants}
                            className="mt-2 font-semibold text-[#96BA33] lg:text-[18px]"
                        >
                            {t('highlightDesc')}
                        </motion.div>

                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.3 }}
                            variants={textVariants}
                            className="my-8 lg:my-12 h-px w-full bg-[#D7D7D7]"
                        />
                    </div>

                    {/* ── Main content: Slider → Accordion on mobile ──────── */}
                    <div className="flex flex-col lg:flex-row justify-between gap-10 lg:gap-14">
                        {/* Slider – first on mobile */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.3 }}
                            variants={scaleUp}
                            className="order-1 lg:order-1 relative mb-10 lg:mb-0 h-[340px] sm:h-[400px] md:h-[480px] lg:h-[650px] w-full lg:w-[40%] rounded-lg overflow-hidden"
                        >
                            <Slider {...(window.innerWidth < 1024 ? mobileSettings : kandySliderSettings)}>
                                {resortImages.map((image, index) => (
                                    <div key={index} className={`${window.innerWidth < 1024 ? 'px-2' : ''}`}>
                                        <div className="relative h-full w-full overflow-hidden rounded-lg">
                                            <img
                                                src={image.src}
                                                alt={image.alt}
                                                className="h-full w-full object-cover"
                                                onError={(e) => {
                                                    e.target.src = 'https://via.placeholder.com/400x550?text=Image+Not+Found';
                                                }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </Slider>
                        </motion.div>

                        {/* Accordion */}
                        <motion.div
                            variants={staggerContainer}
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true, amount: 0.2 }}
                            className="w-full lg:w-[60%] space-y-4 order-2 lg:order-2"
                        >
                            {services.map((service, index) => (
                                <motion.div
                                    key={index}
                                    variants={accordionItem}
                                    className="cursor-pointer rounded-lg border border-gray-600 bg-[#1F1F1F] p-4 transition hover:border-white"
                                    onClick={() => toggleService(index)}
                                    role="button"
                                    aria-expanded={activeIndex === index}
                                    aria-controls={`service-${index}`}
                                >
                                    <h2 className="flex items-center justify-between text-[16px] sm:text-[17px] lg:text-[20px] font-semibold">
                                        {service.title}
                                        <span className="text-[24px] lg:text-[25px]">
                                            {activeIndex === index ? '−' : '+'}
                                        </span>
                                    </h2>
                                    {activeIndex === index && (
                                        <p
                                            id={`service-${index}`}
                                            className="mt-2 text-[14px] sm:text-[15px] lg:text-[16px] text-[#C2C2C2] leading-relaxed"
                                        >
                                            {service.desc}
                                        </p>
                                    )}
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Our Rooms Section */}
            <section className="bg-[#1F1F1F]">
                <div className="container font-redhat text-white">
                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="flex items-center gap-5">
                        <div className="h-px w-[40px] bg-[#BF943A]"></div>
                        <h1 className="text-[22px] font-semibold uppercase md:text-[30px] lg:text-[40px]">
                            {t('roomsTitle')}
                        </h1>
                    </motion.div>

                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="mt-3 max-w-4xl text-[#96BA33] text-[15px] md:text-[17px] lg:text-[18px]">
                        {t('roomsDesc')}
                    </motion.div>

                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="my-6 h-px w-full bg-[#2A2A2A] lg:my-10"></motion.div>

                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-2">
                        {rooms.map((room, index) => (
                            <motion.div
                                key={room.title}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true, amount: 0.2 }}
                                variants={scaleUp}
                                className="group relative overflow-hidden rounded-2xl border border-[#2D2D2D] bg-gradient-to-b from-[#1B1B1B] to-[#0F0F0F] shadow-xl"
                            >
                                <div className="relative h-[350px] w-full overflow-hidden">
                                    <img
                                        src={room.image}
                                        alt={room.title}
                                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                                        onError={(e) => { e.target.src = 'https://via.placeholder.com/500x300?text=Room'; }}
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                                    <div className="absolute bottom-3 left-3 rounded-full bg-white/15 px-3 py-1 text-[12px] font-semibold uppercase tracking-wide text-white">
                                        Riverside Escape
                                    </div>
                                </div>

                                <div className="flex h-full flex-col gap-3 px-5 py-6">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-[20px] font-semibold lg:text-[22px]">{room.title}</h3>
                                        <span className="text-[#BF943A] text-[14px] font-semibold">{t('bookableNow')}</span>
                                    </div>
                                    <p className="text-[14px] text-[#C9C9C9] leading-relaxed">
                                        {room.desc}
                                    </p>
                                    <div className="flex items-center justify-between gap-3 flex-wrap">
                                        <div className="flex items-center gap-2 text-[12px] text-[#9BC24D] font-semibold uppercase tracking-wide whitespace-nowrap">
                                            <span className="inline-block h-2 w-2 rounded-full bg-[#9BC24D]"></span>
                                            River breeze ready
                                        </div>
                                        <Link
                                            to={room.link}
                                            className="rounded-full border border-[#BF943A] bg-white text-black px-5 py-2.5 text-[14px] font-semibold transition hover:scale-105 hover:bg-[#BF943A] hover:text-white shrink-0"
                                        >
                                            {t('viewRoom')}
                                        </Link>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Achievements Section */}
            <section className="bg-[#1F1F1F]">
                <div className="container relative font-redhat text-white">
                    <div className="h-auto">
                        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={scaleUp} className="relative z-10 mt-12 rounded-3xl bg-gradient-to-r from-[#285509] to-[#12291f] py-10">
                            <h1 className="bg-gradient-to-r from-[#BF943A] to-white bg-clip-text text-center text-[30px] font-semibold text-transparent lg:text-[48px]">
                                {t('achievementsTitle')}
                            </h1>
                            <div className="mt-10 grid grid-cols-1 gap-8 text-center md:grid-cols-4">
                                {achievements.map((item, index) => (
                                    <div key={index} className="flex flex-col items-center gap-2">
                                        <div className="text-[24px] font-semibold">{item.title}</div>
                                        <div className="h-px w-[150px] bg-[#D7D7D7]"></div>
                                        <div className="text-[48px] font-bold text-[#BF943A]">{item.count}</div>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Experiences Section */}
            <section className="bg-[#1F1F1F] py-12">
                <div className="container relative font-redhat text-white">
                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="flex items-center gap-5">
                        <div className="h-px w-[40px] bg-[#D7D7D7]"></div>
                        <h1 className="text-[25px] font-semibold uppercase md:text-[30px] lg:text-[40px]">
                            {t('experiencesTitle')}
                        </h1>
                    </motion.div>

                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="mt-2 justify-between lg:flex">
                        <div className="w-full text-[12px] font-semibold text-[#96BA33] md:text-[16px] lg:w-[60%] lg:text-[18px]">
                            {t('experiencesDesc')}
                        </div>
                        <div className="mt-5 w-full justify-end lg:mt-0 lg:flex lg:w-[50%]">
                            <Link
                                to="/river/search"
                                className="flex w-auto items-center justify-center gap-2 rounded-full bg-white px-5 py-2 text-[18px] font-semibold text-black lg:text-[20px] hover:bg-gray-200 transition"
                            >
                                {t('planYourStay')}
                            </Link>
                        </div>
                    </motion.div>

                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="my-6 h-px w-full bg-[#D7D7D7] lg:my-12"></motion.div>

                    <div className="mt-5 hidden grid-cols-1 gap-8 lg:grid lg:grid-cols-4">
                        {experiences.map((item, index) => (
                            <motion.div key={index} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={scaleUp}
                                className="group relative h-[350px] w-full rounded-lg border border-gray-600 bg-[#285509] shadow-lg transition hover:border-[#BF943A]">
                                <img
                                    src={item.img}
                                    alt={item.title}
                                    className="h-[200px] w-full rounded-t-lg object-cover transition-transform duration-500 group-hover:scale-105"
                                    onError={(e) => { e.target.src = 'https://via.placeholder.com/150x150?text=Image+Not+Found'; }}
                                />
                                <div className="flex h-[150px] flex-col items-center justify-between p-4">
                                    <h2 className="text-center text-[20px] font-semibold text-white">
                                        {item.title}
                                    </h2>
                                    <p className="text-center text-[14px] text-[#C2C2C2]">
                                        {item.desc}
                                    </p>
                                    <Link
                                        to={item.link}
                                        className="flex h-[40px] w-[40px] items-center justify-center rounded-full border-[1.5px] border-[#BF943A] text-[14px] font-semibold text-[#BF943A] hover:bg-[#BF943A] hover:text-black transition"
                                    >
                                        →
                                    </Link>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="block md:hidden">
                        <Slider {...mobileSettings}>
                            {experiences.map((item, index) => (
                                <div key={index} className="px-4">
                                    <motion.div variants={scaleUp} className="group relative h-[450px] w-full rounded-lg border border-gray-600 bg-[#12291f] shadow-lg transition hover:border-[#BF943A]">
                                        <img src={item.img} alt={item.title} className="h-[200px] w-full rounded-t-lg object-cover transition-transform duration-500 group-hover:scale-105" />
                                        <div className="flex h-[250px] flex-col items-center justify-between p-4">
                                            <h2 className="text-center text-[20px] font-semibold text-white">{item.title}</h2>
                                            <p className="text-center text-[14px] text-[#C2C2C2]">{item.desc}</p>
                                            <Link to={item.link} className="flex h-[40px] w-[40px] items-center justify-center rounded-full border-[1.5px] border-[#BF943A] text-[14px] font-semibold text-[#BF943A] hover:bg-[#BF943A] hover:text-black transition">
                                                →
                                            </Link>
                                        </div>
                                    </motion.div>
                                </div>
                            ))}
                        </Slider>
                    </motion.div>

                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="hidden md:block lg:hidden">
                        <Slider {...tabSettings}>
                            {experiences.map((item, index) => (
                                <div key={index} className="px-4">
                                    <motion.div variants={scaleUp} className="group relative h-[300px] w-full rounded-lg border border-gray-600 bg-[#12291f] shadow-lg transition hover:border-[#BF943A]">
                                        <img src={item.img} alt={item.title} className="h-[150px] w-full rounded-t-lg object-cover transition-transform duration-500 group-hover:scale-105" />
                                        <div className="flex h-[150px] flex-col items-center justify-between p-4">
                                            <h2 className="text-center text-[20px] font-semibold text-white">{item.title}</h2>
                                            <p className="text-center text-[14px] text-[#C2C2C2]">{item.desc}</p>
                                            <Link to={item.link} className="flex h-[40px] w-[40px] items-center justify-center rounded-full border-[1.5px] border-[#BF943A] text-[14px] font-semibold text-[#BF943A] hover:bg-[#BF943A] hover:text-black transition">
                                                →
                                            </Link>
                                        </div>
                                    </motion.div>
                                </div>
                            ))}
                        </Slider>
                    </motion.div>
                </div>
            </section>

            {/* Gallery Section */}
            <section className="bg-[#1F1F1F] py-12">
                <div className="container relative font-redhat text-white">
                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="flex items-center gap-5">
                        <div className="h-px w-[40px] bg-[#D7D7D7]"></div>
                        <h1 className="text-[25px] font-semibold uppercase md:text-[30px] lg:text-[40px]">
                            {t('galleryTitle')}
                        </h1>
                    </motion.div>

                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="mt-2 w-full font-semibold text-[#96BA33] lg:w-[50%] lg:text-[18px]">
                        {t('galleryDesc')}
                    </motion.div>

                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="my-6 h-px w-full bg-[#D7D7D7] lg:my-12"></motion.div>

                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="mt-5 block md:hidden">
                        <Slider {...galleryMobileSettings}>
                            {galleryImages.map((image, index) => (
                                <div key={index} className="px-2">
                                    <div className="relative h-[300px] w-full overflow-hidden rounded-lg">
                                        <img src={image.src} alt={image.alt} className="h-full w-full object-cover" />
                                    </div>
                                </div>
                            ))}
                        </Slider>
                    </motion.div>

                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="mt-5 hidden md:grid md:grid-cols-2 lg:hidden gap-4">
                        {galleryImages.map((image, index) => (
                            <div key={index} className="relative h-[250px] w-full overflow-hidden rounded-lg">
                                <img src={image.src} alt={image.alt} className="h-full w-full object-cover" />
                            </div>
                        ))}
                    </motion.div>

                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="mt-5 hidden lg:grid grid-cols-3 gap-4">
                        {galleryImages.map((image, index) => (
                            <div key={index} className="relative h-[420px] w-full overflow-hidden rounded-lg">
                                <img src={image.src} alt={image.alt} className="h-full w-full object-cover" />
                            </div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* Boat Rides Section */}
            <section className="bg-gradient-to-b from-[#1F1F1F] to-[#0F0F0F] py-12 md:py-16 lg:py-24">
                <div className="container font-redhat text-white">

                    {/* === MOBILE / SM / MD layout (stacked: label → title → image → text) === */}
                    <div className="block lg:hidden space-y-8 px-4">
                        {/* Label */}
                        <div className="flex items-center gap-4">
                            <div className="h-px w-[50px] bg-gradient-to-r from-[#96BA33] to-[#BF943A]"></div>
                            <span className="text-[14px] uppercase tracking-widest font-semibold text-[#96BA33]">
                                {t('boatLabel')}
                            </span>
                        </div>

                        {/* Title */}
                        <h2 className="text-[32px] sm:text-[40px] md:text-[48px] font-bold leading-tight bg-gradient-to-r from-[#96BA33] via-[#BF943A] to-white bg-clip-text text-transparent">
                            {t('boatTitle')}
                        </h2>

                        {/* Image */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.3 }}
                            // eslint-disable-next-line no-undef

                            className="relative rounded-2xl overflow-hidden shadow-2xl"
                        >
                            <div className="relative aspect-[4/3] sm:aspect-[3/2] md:aspect-video">
                                <img
                                    src={boatRideImage}
                                    alt={t('boatImageAlt')}
                                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-1000 hover:scale-110"
                                    onError={(e) => { e.target.src = 'https://via.placeholder.com/800x600?text=Boat+Ride'; }}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                                <div className="absolute bottom-5 left-5 w-16 h-16 border-b-2 border-l-2 border-[#96BA33] rounded-bl-2xl"></div>
                                <div className="absolute top-5 right-5 w-16 h-16 border-t-2 border-r-2 border-[#96BA33] rounded-tr-2xl"></div>
                            </div>
                        </motion.div>

                        {/* Description + Features */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.3 }}
                            // eslint-disable-next-line no-undef

                            className="space-y-6"
                        >
                            <div className="space-y-4 text-gray-300">
                                <p className="text-[16px] md:text-[18px] leading-relaxed">
                                    {t('boatDesc1')}
                                </p>
                                <p className="text-[15px] md:text-[16px] leading-relaxed text-gray-400">
                                    {t('boatDesc2')}
                                </p>
                            </div>

                            <div className="space-y-3 pt-2">
                                {[
                                    { icon: '🚤', key: 'boatFeature1' },
                                    { icon: '🦜', key: 'boatFeature2' },
                                    { icon: '🌅', key: 'boatFeature3' },
                                    { icon: '👨‍💼', key: 'boatFeature4' }
                                ].map((item, idx) => (
                                    <motion.div
                                        key={idx}
                                        initial="hidden"
                                        whileInView="visible"
                                        viewport={{ once: true, amount: 0.4 }}
                                        transition={{ delay: idx * 0.1 }}
                                        variants={scaleUp}
                                        className="flex items-center gap-4 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-all duration-300 border border-[#96BA33]/20 hover:border-[#96BA33]/40"
                                    >
                                        <span className="text-[24px]">{item.icon}</span>
                                        <span className="text-[15px] md:text-[16px] text-gray-300">{t(item.key)}</span>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>
                    </div>

                    {/* === DESKTOP / LG layout (side by side) === */}
                    <div className="hidden lg:block">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 xl:gap-16 items-center">
                            <motion.div
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true, amount: 0.3 }}
                                variants={fadeInLeft}
                                className="space-y-6 order-2 lg:order-1"
                            >
                                <div className="flex items-center gap-5">
                                    <div className="h-px w-[50px] bg-gradient-to-r from-[#96BA33] to-[#BF943A]"></div>
                                    <span className="text-[16px] uppercase tracking-widest font-semibold text-[#96BA33]">
                                        {t('boatLabel')}
                                    </span>
                                </div>

                                <h2 className="text-[48px] lg:text-[60px] font-bold leading-tight bg-gradient-to-r from-[#96BA33] via-[#BF943A] to-white bg-clip-text text-transparent">
                                    {t('boatTitle')}
                                </h2>

                                <div className="space-y-4 text-gray-300">
                                    <p className="text-[18px] lg:text-[20px] leading-relaxed">
                                        {t('boatDesc1')}
                                    </p>
                                    <p className="text-[16px] lg:text-[18px] leading-relaxed text-gray-400">
                                        {t('boatDesc2')}
                                    </p>
                                </div>

                                <div className="space-y-3 pt-4">
                                    {[
                                        { icon: '🚤', key: 'boatFeature1' },
                                        { icon: '🦜', key: 'boatFeature2' },
                                        { icon: '🌅', key: 'boatFeature3' },
                                        { icon: '👨‍💼', key: 'boatFeature4' }
                                    ].map((item, idx) => (
                                        <motion.div
                                            key={idx}
                                            initial="hidden"
                                            whileInView="visible"
                                            viewport={{ once: true, amount: 0.3 }}
                                            transition={{ delay: idx * 0.1 }}
                                            variants={scaleUp}
                                            className="flex items-center gap-4 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-all duration-300 border border-[#96BA33]/20 hover:border-[#96BA33]/40"
                                        >
                                            <span className="text-[24px]">{item.icon}</span>
                                            <span className="text-[15px] md:text-[16px] text-gray-300">{t(item.key)}</span>
                                        </motion.div>
                                    ))}
                                </div>
                            </motion.div>

                            <motion.div
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true, amount: 0.3 }}
                                variants={fadeInRight}
                                className="relative order-1 lg:order-2"
                            >
                                <div className="relative h-[500px] lg:h-[700px] rounded-3xl overflow-hidden shadow-2xl group">
                                    <img
                                        src={boatRideImage}
                                        alt={t('boatImageAlt')}
                                        className="h-full w-full object-cover transition-transform duration-1000 group-hover:scale-110"
                                        onError={(e) => { e.target.src = 'https://via.placeholder.com/800x700?text=Boat+Ride'; }}
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                                    <div className="absolute bottom-6 left-6 w-20 h-20 border-b-2 border-l-2 border-[#96BA33] rounded-bl-3xl"></div>
                                    <div className="absolute top-6 right-6 w-20 h-20 border-t-2 border-r-2 border-[#96BA33] rounded-tr-3xl"></div>
                                </div>
                            </motion.div>
                        </div>
                    </div>

                </div>
            </section>

            {/* Pool Area Section */}
            <section className="bg-gradient-to-b from-[#0F0F0F] to-[#1F1F1F] py-12 md:py-16 lg:py-24">
                <div className="container font-redhat text-white">
                    {/* Mobile-first header: label + title */}
                    <div className="lg:hidden space-y-6 mb-10">
                        <div className="flex items-center gap-5">
                            <div className="h-px w-[50px] bg-gradient-to-r from-[#96BA33] to-[#BF943A]"></div>
                            <span className="text-[14px] sm:text-[16px] uppercase tracking-widest font-semibold text-[#96BA33]">
                                {t('poolLabel')}
                            </span>
                        </div>

                        <h2 className="text-4xl sm:text-5xl font-bold leading-tight bg-gradient-to-r from-[#96BA33] via-[#BF943A] to-white bg-clip-text text-transparent">
                            {t('poolTitle')}
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                        {/* Image – comes after title on mobile */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.3 }}
                            variants={fadeInLeft}
                            className="relative order-2 lg:order-1"
                        >
                            <div className="relative h-[380px] sm:h-[460px] md:h-[520px] lg:h-[700px] rounded-3xl overflow-hidden shadow-2xl group">
                                <img
                                    src={poolArea}
                                    alt={t('poolImageAlt')}
                                    className="h-full w-full object-cover transition-transform duration-1000 group-hover:scale-110"
                                    onError={(e) => { e.target.src = 'https://via.placeholder.com/800x700?text=Pool+Area'; }}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>

                                {/* Decorative corners */}
                                <div className="absolute bottom-6 right-6 w-16 h-16 sm:w-20 sm:h-20 border-b-2 border-r-2 border-[#96BA33] rounded-br-3xl"></div>
                                <div className="absolute top-6 left-6 w-16 h-16 sm:w-20 sm:h-20 border-t-2 border-l-2 border-[#96BA33] rounded-tl-3xl"></div>
                            </div>
                        </motion.div>

                        {/* Text content */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.3 }}
                            variants={fadeInRight}
                            className="space-y-6 order-3 lg:order-2"
                        >
                            {/* Desktop label + title – hidden on < lg */}
                            <div className="hidden lg:flex items-center gap-5">
                                <div className="h-px w-[50px] bg-gradient-to-r from-[#96BA33] to-[#BF943A]"></div>
                                <span className="text-[14px] lg:text-[16px] uppercase tracking-widest font-semibold text-[#96BA33]">
                                    {t('poolLabel')}
                                </span>
                            </div>

                            <h2 className="hidden lg:block text-[36px] lg:text-[48px] xl:text-[60px] font-bold leading-tight bg-gradient-to-r from-[#96BA33] via-[#BF943A] to-white bg-clip-text text-transparent">
                                {t('poolTitle')}
                            </h2>

                            <div className="space-y-4 text-gray-300">
                                <p className="text-[16px] sm:text-[18px] lg:text-[20px] leading-relaxed">
                                    {t('poolDesc1')}
                                </p>
                                <p className="text-[15px] sm:text-[16px] lg:text-[18px] leading-relaxed text-gray-400">
                                    {t('poolDesc2')}
                                </p>
                            </div>

                            <div className="space-y-3 pt-4">
                                {[
                                    { icon: '🏊', key: 'poolFeature1' },
                                    { icon: '🌴', key: 'poolFeature2' },
                                    { icon: '🍹', key: 'poolFeature3' },
                                    { icon: '🛋️', key: 'poolFeature4' }
                                ].map((item, idx) => (
                                    <motion.div
                                        key={idx}
                                        initial="hidden"
                                        whileInView="visible"
                                        viewport={{ once: true, amount: 0.3 }}
                                        transition={{ delay: idx * 0.1 }}
                                        variants={scaleUp}
                                        className="flex items-center gap-4 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-all duration-300 border border-[#96BA33]/20 hover:border-[#96BA33]/40"
                                    >
                                        <span className="text-[22px] sm:text-[24px]">{item.icon}</span>
                                        <span className="text-[15px] sm:text-[16px] text-gray-300">{t(item.key)}</span>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="bg-[#1F1F1F] py-12">
                <div className="container font-redhat text-white">
                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="flex items-center gap-5">
                        <div className="h-px w-[40px] bg-[#D7D7D7]"></div>
                        <h1 className="text-[20px] font-semibold uppercase md:text-[30px] lg:text-[40px]">
                            {t('testimonialsTitle')}
                        </h1>
                    </motion.div>

                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={textVariants} className="my-6 h-px w-full bg-[#D7D7D7] lg:my-12"></motion.div>

                    <div className="py-10 min-h-[300px]">
                        <div className="elfsight-app-9bc3a085-3b18-42c9-af0c-abac4af3553a"></div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home2;